/**
 * Client fetch helpers for `/api/timeline` (Clerk session cookies).
 */

import {
  isApiFailureBody,
  isApiSuccessBody,
  readApiErrorMessage,
} from "@/lib/api/api-envelope"

export type TimelineSaveWire = {
  eventId: string
  createdAt: string
}

export type TimelineListResponse = {
  saves: TimelineSaveWire[]
}

export async function fetchTimelineSaves(): Promise<TimelineSaveWire[]> {
  const res = await fetch("/api/timeline", { cache: "no-store" })
  const body: unknown = await res.json()
  if (!res.ok || isApiFailureBody(body)) {
    throw new Error(
      readApiErrorMessage(body, `GET /api/timeline failed: ${res.status}`),
    )
  }
  if (!isApiSuccessBody<TimelineListResponse>(body)) {
    throw new Error(`GET /api/timeline failed: malformed response`)
  }
  /** First page only; use `meta.nextCursor` + `?cursor=` for additional pages. */
  return body.data.saves ?? []
}

export async function postTimelineSave(eventId: string): Promise<void> {
  const res = await fetch("/api/timeline", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ eventId }),
  })
  const body: unknown = await res.json()
  if (!res.ok || isApiFailureBody(body)) {
    throw new Error(
      readApiErrorMessage(body, `POST /api/timeline failed: ${res.status}`),
    )
  }
}

export async function deleteTimelineSave(eventId: string): Promise<boolean> {
  const res = await fetch(
    `/api/timeline/${encodeURIComponent(eventId)}`,
    { method: "DELETE" },
  )
  const body: unknown = await res.json()
  if (!res.ok || isApiFailureBody(body)) {
    throw new Error(
      readApiErrorMessage(
        body,
        `DELETE /api/timeline/[id] failed: ${res.status}`,
      ),
    )
  }
  if (!isApiSuccessBody<{ ok: true; removed: boolean }>(body)) {
    throw new Error(`DELETE /api/timeline/[id] failed: malformed response`)
  }
  return Boolean(body.data.removed)
}
